import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import _ from "lodash";

import { connect } from "react-redux";
import PActions from "../../Stores/redux/Persisted/Actions";
import UnpActions from "../../Stores/redux/Unpersisted/Actions";

import Modal from "../../Components/Modal/Modal";
import AutoCompleteDropdownStatic from "../../Components/Input/AutoCompleteDropdownStatic";
import api from "../../Services/Api/api";
import etcModule from "../../Modules/etc/etc-module";
import AutoCompleteDropdownDynamic from "../../Components/Input/AutoCompleteDropdownDynamic";

const NewVisitForm = ({
  isOpen,
  onClose,
  locationData,
  setScreenState,
  user,
  onSuccess,
  visitors,
}) => {
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [subdivisionOptions, setSubdivisionOptions] = useState(null);
  const [blockOptions, setBlockOptions] = useState(null);
  const [circleOptions, setCircleOptions] = useState(null);
  const [lacOptions, setLacOptions] = useState(null);
  const [officeOptions, setOfficeOptions] = useState(null);
  const [schemeOptions, setSchemeOptions] = useState(null);

  const [formData, setFormData] = useState({
    district: user.district._id,
    subdivision: null,
    block: null,
    circle: null,
    lac: null,
    visitor: "",
    office: "",
    scheme: "",
  });

  const totalSteps = 2;

  useEffect(() => {
    if (isOpen && !locationData) {
      loadLocationData();
    }
    if (isOpen) {
      loadVisitors();
    }
  }, [isOpen]);

  useEffect(() => {
    if (locationData) {
      setSubdivisionOptions([
        {
          items: locationData?.subdivisions?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setBlockOptions([
        {
          items: locationData?.blocks?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setCircleOptions([
        {
          items: locationData?.circles?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setLacOptions([
        {
          items: locationData?.lacs?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
    }
  }, [locationData]);

  useEffect(() => {
    if (step === 2) {
      resetOfficesAndSchemes();
    }
  }, [step]);

  const resetOfficesAndSchemes = async () => {
    setOfficeOptions(null);
    try {
      const officeP = api.get("v1/district-admin/office", {
        filter: JSON.stringify({
          where: _.pick(formData, [
            "district",
            "subdivision",
            "block",
            "circle",
            "lac",
          ]),
          select: "name",
        }),
      });
      const schemesP = api.get("v1/district-admin/scheme", {
        filter: JSON.stringify({
          where: _.pick(formData, [
            "district",
            "subdivision",
            "block",
            "circle",
            "lac",
          ]),
          select: "name",
        }),
      });

      const { offices } = await officeP;
      const { schemes } = await schemesP;

      setOfficeOptions([
        {
          items: offices.map((x) => ({ value: x._id, label: x.name })),
        },
      ]);

      setSchemeOptions([
        {
          items: schemes.map((x) => ({ value: x._id, label: x.name })),
        },
      ]);
    } catch (e) {
      toast.error("Error loading office list: " + e.message);
    }
  };

  const loadLocationData = async () => {
    try {
      const locationData = await api.get("v1/appdata/location-data", {
        district: user.district._id,
      });
      setScreenState(
        { locationData: { ...locationData, uid: Date.now() } },
        false,
        "APP"
      );
    } catch (e) {
      toast.error("Error loading data: " + e.message);
    }
  };

  const loadVisitors = async (query) => {
    try {
      const { users } = await api.get("v1/district-admin/user", {
        filter: JSON.stringify({
          where: {
            district: user.district._id,
            permissions: "visitor",
          },
          select: "name phone",
        }),
      });

      console.log({ users });
      const visitors = users.map((x) => ({
        value: x._id,
        label: `${x.name} - ${x.phone}`,
      }));

      setScreenState({ visitors });
    } catch (e) {
      toast.error("Error fetching users: " + e.message);
      return [];
    }
  };

  // Handle Next / Previous Steps
  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => prev - 1);

  // Handle Input Change
  const handleChange = (name, value) =>
    setFormData({ ...formData, [name]: value });

  // Submit Form
  const handleSubmit = async () => {
    try {
      if (loading) return;

      setError(null);
      setLoading(true);

      const { visit } = await api.post(
        "v1/district-admin/visit",
        _.pick(formData, ["district", "office", "scheme", "visitor"])
      );
      toast.success("Visit created successfully");

      setLoading(false);
      onClose(); // Close modal after submit
      if (onSuccess) onSuccess(visit);
    } catch (e) {
      setError(e.message);
      setLoading(false);
      toast.error("Error: " + e.message);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Visit"
      showFooter={false}
      closeOnOutsideClick={false}
    >
      {/* Step Indicator */}
      <div className="step-indicator">
        Step {step} of {totalSteps}
      </div>

      {/* Step 1 - Location Selection */}
      {step === 1 && (
        <>
          <div className="form-step">
            <label>District</label>
            <input type="text" value={user.district.name} disabled />

            <label>Subdivision</label>
            <AutoCompleteDropdownStatic
              options={subdivisionOptions}
              placeholder="Select Subdivision"
              value={formData.subdivision}
              onSelect={(value) => handleChange("subdivision", value)}
              loading={!subdivisionOptions}
            />

            <label>Block</label>
            <AutoCompleteDropdownStatic
              options={blockOptions}
              placeholder="Select Block"
              value={formData.block}
              onSelect={(value) => handleChange("block", value)}
              loading={!blockOptions}
            />

            <label>Circle</label>
            <AutoCompleteDropdownStatic
              options={circleOptions}
              placeholder="Select Circle"
              value={formData.circle}
              onSelect={(value) => handleChange("circle", value)}
              loading={!circleOptions}
            />

            <label>LAC</label>
            <AutoCompleteDropdownStatic
              options={lacOptions}
              placeholder="Select LAC"
              value={formData.lac}
              onSelect={(value) => handleChange("lac", value)}
              loading={!lacOptions}
            />
          </div>

          <div className="modal-footer">
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            {formData.district &&
              formData.subdivision &&
              formData.block &&
              formData.circle &&
              formData.lac && (
                <button className="next-btn" onClick={nextStep}>
                  Next
                </button>
              )}
          </div>
        </>
      )}

      {/* Step 2 - Visit Details */}
      {step === 2 && (
        <>
          <div className="form-step">
            <label>Visitor</label>
            <AutoCompleteDropdownStatic
              options={[{ items: visitors }]}
              placeholder="Select Visitor"
              value={formData.visitor || ""}
              onSelect={(value) => handleChange("visitor", value)}
              loading={!visitors}
            />

            <div className="toggle-container">
              <button
                className={formData.scheme !== null ? "active" : ""}
                onClick={() =>
                  setFormData({ ...formData, scheme: "", office: null })
                }
              >
                Scheme
              </button>
              <button
                className={formData.office !== null ? "active" : ""}
                onClick={() =>
                  setFormData({ ...formData, scheme: null, office: "" })
                }
              >
                Office
              </button>
            </div>

            {/* Scheme Selection (Shown Only if Selected) */}
            {formData.scheme !== null && (
              <>
                <label>Scheme</label>
                <AutoCompleteDropdownStatic
                  options={schemeOptions}
                  loading={!schemeOptions}
                  placeholder="Select Scheme"
                  value={formData.scheme || ""}
                  onSelect={(value) => {
                    setFormData({ ...formData, scheme: value, office: null });
                  }}
                />
              </>
            )}
            {/* Office Selection (Shown Only if Selected) */}
            {formData.office !== null && (
              <>
                <label>Office</label>
                <AutoCompleteDropdownStatic
                  options={officeOptions}
                  loading={!officeOptions}
                  placeholder="Select Office"
                  value={formData.office || ""}
                  onSelect={(value) => {
                    setFormData({ ...formData, scheme: null, office: value });
                  }}
                />
              </>
            )}
          </div>

          <div className="modal-footer">
            <button className="prev-btn" onClick={prevStep}>
              Previous
            </button>
            {formData.visitor && (formData.scheme || formData.office) && (
              <button className="submit-btn" onClick={handleSubmit}>
                {loading ? "Loading.." : "Submit"}
              </button>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  locationData: state.vState.APP.locationData,
  user: state.pState.AUTH.user,
  visitors: state.vState.VISITS_SCREEN.visitors,
});

const mapDispatchToProps = (dispatch) => ({
  setScreenState: (obj, persist = false, screenName = "VISITS_SCREEN") =>
    persist
      ? dispatch(PActions.setPScreenState(screenName, obj))
      : dispatch(UnpActions.setVScreenState(screenName, obj)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NewVisitForm);
